// Error handling utilities for the parent dashboard

export const handleAPIError = (error, context = '') => {
    console.error(`API Error ${context}:`, error);
    
    // Check for specific error types
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // Token expired or invalid
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
        return 'Session expired. Please log in again.';
    }
    
    if (error.message.includes('403') || error.message.includes('Forbidden')) {
        return 'Access denied. Please contact your school administrator.';
    }
    
    if (error.message.includes('404') || error.message.includes('Not Found')) {
        return 'Requested information not found.';
    }
    
    if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        return 'Server error. Please try again later.';
    }
    
    if (error.message.includes('Network Error') || error.name === 'TypeError') {
        return 'Network connection error. Please check your internet connection.';
    }
    
    // Generic error message
    return error.message || 'An unexpected error occurred. Please try again.';
};

export const showErrorNotification = (message, duration = 5000) => {
    // Create a simple error notification
    const notification = document.createElement('div');
    notification.className = `
        fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50
        transform transition-transform duration-300 translate-x-full
    `;
    notification.innerHTML = `
        <div class="flex items-center gap-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span>${message}</span>
        </div>
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
        notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Animate out and remove
    setTimeout(() => {
        notification.style.transform = 'translateX(full)';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 300);
    }, duration);
};

export const showSuccessNotification = (message, duration = 3000) => {
    // Create a simple success notification
    const notification = document.createElement('div');
    notification.className = `
        fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50
        transform transition-transform duration-300 translate-x-full
    `;
    notification.innerHTML = `
        <div class="flex items-center gap-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span>${message}</span>
        </div>
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
        notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Animate out and remove
    setTimeout(() => {
        notification.style.transform = 'translateX(full)';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 300);
    }, duration);
};

export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    
    for (let i = 0; i < maxRetries; i++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            
            // Don't retry on authentication errors
            if (error.message.includes('401') || error.message.includes('403')) {
                throw error;
            }
            
            // Wait before retrying (exponential backoff)
            if (i < maxRetries - 1) {
                const delay = baseDelay * Math.pow(2, i);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }
    
    throw lastError;
};